import { Button } from '@bambi/component-library/src/components/Button/Button';
import { ContextFormFieldNumber } from '@bambi/component-library/src/components/forms/context/ContextFormFieldNumber';
import { ContextFormFieldText } from '@bambi/component-library/src/components/forms/context/ContextFormFieldText';
import { ContextFormFieldTextArea } from '@bambi/component-library/src/components/forms/context/ContextFormFieldTextArea';
import { DefaultFormFieldLayout } from '@bambi/component-library/src/components/forms/DefaultFormFieldLayout/DefaultFormFieldLayout';
import { FormFieldContainer } from '@bambi/component-library/src/components/forms/layout/FormFieldContainer';
import { FormFieldSet } from '@bambi/component-library/src/components/forms/layout/FormFieldSet';
import { Select } from '@bambi/component-library/src/components/inputs/Select/Select';
import type { SelectOption } from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';
import { useEffect, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { assignDefaultFormValues } from './assignDefaultFormValues';
import { DeleteAction } from './DeleteAction';
import type { FormValues } from './FormValues';
import { useFeature } from 'src/app/FeatureToggle';
import { useAppSelector } from 'src/app/store/hooks';
import type { RootState } from 'src/app/store/types';
import { states } from 'src/common/data/states';
import { usePayersListQuery } from 'src/common/external/bambi-api/bambiApi';
import { FormField } from 'src/common/FormField';
import { FormFieldController } from 'src/common/FormField/FormFieldController';
import { ContextFormFieldLocationTypeahead } from 'src/common/FormField/v2/context/ContextFormFieldLocationTypeahead';
import { ContextFormFieldSwitch } from 'src/common/FormField/v2/context/ContextFormFieldSwitch';
import { FormSubmitButton } from 'src/common/FormSubmitButton';
import { MultiSelectPayerField } from 'src/common/MultiSelectPayerContextField';

const vehicleTypeOptions = [
  { value: 'wheelchair', label: 'Wheelchair' },
  { value: 'ambulatory', label: 'Ambulatory' },
  { value: 'stretcher', label: 'Stretcher' },
];

interface VehicleFormStep1Props {
  onSubmit: (values: FormValues) => void;
  onCancel: () => void;
  onDelete: () => void;
  onFormDirtyChange: (isDirty: boolean) => void;
}

export function VehicleFormStep1({
  onSubmit,
  onCancel,
  onDelete,
  onFormDirtyChange,
}: VehicleFormStep1Props) {
  const { isEnabled: isPayerAllowedVehiclesAndDriversEnabled } = useFeature(
    'payer allowed vehicles and drivers'
  );

  const selectedVehicle = useAppSelector(
    (state: RootState) => state.fleet.selectedVehicle
  );

  const methods = useForm({
    defaultValues: assignDefaultFormValues(selectedVehicle),
  });

  useEffect(() => {
    onFormDirtyChange(methods.formState.isDirty);
  }, [methods.formState.isDirty, onFormDirtyChange]);

  const { control, handleSubmit, reset, getValues } = methods;

  const { data: payersWithAllowedVehicles } = usePayersListQuery(
    {
      payerSettingsAllowOnlySpecificDrivers: true,
    },
    {
      skip: !isPayerAllowedVehiclesAndDriversEnabled,
    }
  );

  const showAllowedPayers = useMemo(
    () =>
      isPayerAllowedVehiclesAndDriversEnabled &&
      !!payersWithAllowedVehicles?.results.length,
    [isPayerAllowedVehiclesAndDriversEnabled, payersWithAllowedVehicles]
  );

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit((val) => {
          onSubmit(getValues());
        })}
        data-testid="vehicle-form-step-1"
      >
        <div className="flex w-[80vw] flex-col gap-4">
          <FormFieldSet
            Fields={
              <>
                <Controller
                  name="category"
                  control={control}
                  render={({ field: { onChange, value } }) => {
                    return (
                      <FormField
                        label="Vehicle Type"
                        type="select"
                        placeholder="Select Vehicle Type"
                        inputProps={{
                          id: 'category',
                        }}
                        onChange={(option) => {
                          onChange(option.value);
                        }}
                        value={
                          value
                            ? vehicleTypeOptions.find((x) => x.value === value)
                            : ''
                        }
                        options={vehicleTypeOptions}
                      />
                    );
                  }}
                />
                <ContextFormFieldSwitch
                  fieldPath="can_be_scheduled"
                  label="Is this vehicle active?"
                />
                <ContextFormFieldText
                  fieldPath="nickname"
                  label="Vehicle Name"
                  rules={{
                    required: 'Vehicle name is required',
                  }}
                />
                <ContextFormFieldLocationTypeahead
                  fieldPath="storage_location"
                  label="Overnight storage location"
                  rules={{
                    // Unfortunately required validation doesn't fire for
                    // location typeaheads. My current theory is something with
                    // ref passing in the impl of LocationTypeahead since there are
                    // two inputs: The query input and the combobox (which has the committed value)
                    // We could try some different base inputs if we can't figure out Combobox
                    // Adding the duplication for now
                    required: 'Storage location is required',
                    validate: (value) => {
                      if (!value.value) {
                        return 'Storage location is required';
                      }
                      return true;
                    },
                  }}
                />
              </>
            }
          />
          <FormFieldSet
            legend="Vehicle Details"
            Fields={
              <>
                <ContextFormFieldText
                  fieldPath="make"
                  label="Make"
                  colSpan={3}
                />
                <ContextFormFieldText
                  fieldPath="model"
                  label="Model"
                  colSpan={3}
                />
                <ContextFormFieldText
                  fieldPath="color"
                  label="Color"
                  colSpan={3}
                />
                <ContextFormFieldNumber
                  fieldPath="year"
                  label="Year"
                  colSpan={3}
                />
                <ContextFormFieldText
                  fieldPath="vin"
                  label="VIN"
                  colSpan={6}
                  rules={{ required: 'Vin is required' }}
                />
                <ContextFormFieldText
                  fieldPath="license_plate"
                  label="License Plate"
                  placeholder="e.g. 123 455 678"
                  colSpan={3}
                  helperText="Between 2 and 7 or 8 (depending on state) characters. Can have 1 hyphen"
                />
                <FormFieldController name="license_plate_state">
                  {({ field, fieldState }) => {
                    return (
                      <FormFieldContainer colSpan={3}>
                        <DefaultFormFieldLayout
                          label="License Plate State"
                          error={fieldState.error?.message?.toString()}
                          inputId="license-plate-state"
                        >
                          <Select
                            placeholder="Select State"
                            options={states}
                            {...field}
                            onChange={(value?: SelectOption) => {
                              field.onChange(value?.value);
                            }}
                            error={fieldState.error?.message?.toString()}
                            testIdSuffix="license-plate-state"
                          />
                        </DefaultFormFieldLayout>
                      </FormFieldContainer>
                    );
                  }}
                </FormFieldController>
                <ContextFormFieldTextArea
                  fieldPath="notes"
                  label="Additional Notes"
                  colSpan={12}
                />
              </>
            }
          />
          {showAllowedPayers && (
            <FormFieldSet
              legend="Allowed Payers"
              Fields={
                <div className="col-span-12 mb-4">
                  <MultiSelectPayerField
                    fieldPath="allowed_payer_ids"
                    canSelectAll={false}
                    payerFilters={{
                      payerSettingsAllowOnlySpecificVehicles: true,
                    }}
                  />
                </div>
              }
            />
          )}
        </div>
        {selectedVehicle.id && <DeleteAction onDelete={onDelete} />}
        <div className="mt-4 grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-6">
          <Button
            className="sm:col-span-3"
            onClick={() => {
              onCancel();
              reset();
            }}
          >
            Cancel
          </Button>
          <FormSubmitButton className="sm:col-span-3" />
        </div>
      </form>
    </FormProvider>
  );
}
